import { useState, useEffect } from "react";
import { apiClient } from "../../apiclient/apiclient";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";

const LoyaltyPoints = () => {
  const [loyaltySettings, setLoyaltySettings] = useState({
    enabled: false,
    amountPerPoint: 100,
    pointValue: 1,
  });
  const [saving, setSaving] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    loadLoyaltySettings();
  }, []);

  const loadLoyaltySettings = async () => {
    try {
      const res = await apiClient.get("/loyalty-settings");
      if (res.data) setLoyaltySettings(res.data);
    } catch (err) {
      console.error("Failed to load loyalty settings:", err);
    }
  };

  const handleSaveLoyaltyClick = () => {
    setConfirmModalOpen(true);
  };

  const confirmSave = async () => {
    setConfirmModalOpen(false);
    try {
      setSaving(true);
      await apiClient.post("/loyalty-settings", loyaltySettings);
      toast.success("Loyalty settings updated successfully");
    } catch (err) {
      toast.error("Failed to save loyalty settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: "0px" }}>
      {/* HEADER */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
        <h1 className="text-xl font-bold text-slate-800">Loyalty Points Program</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure how customers earn and use loyalty points globally.
        </p>
      </div>

      {/* CONTENT */}
      <div className="flex-1 w-full bg-white p-6">
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between max-w-2xl">
          <div>
            <h2 className="text-sm font-bold text-slate-700">Enable Loyalty Program</h2>
            <p className="text-xs text-slate-500 mt-1">
              Toggle this to turn the loyalty program on or off globally.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={loyaltySettings.enabled}
              onChange={(e) =>
                setLoyaltySettings({
                  ...loyaltySettings,
                  enabled: e.target.checked,
                })
              }
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
          </label>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mb-8 p-4 bg-slate-50 rounded-lg border border-slate-200 transition-opacity ${!loyaltySettings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Spend Amount to Earn 1 Point (₹)
            </label>
            <input
              type="number"
              min="1"
              value={loyaltySettings.amountPerPoint}
              onChange={(e) =>
                setLoyaltySettings({
                  ...loyaltySettings,
                  amountPerPoint: Number(e.target.value),
                })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              E.g., Spend ₹100 to earn 1 point.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Value of 1 Point (₹)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={loyaltySettings.pointValue}
              onChange={(e) =>
                setLoyaltySettings({
                  ...loyaltySettings,
                  pointValue: Number(e.target.value),
                })
              }
              className="w-full border border-slate-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              E.g., 1 point equals ₹1 discount.
            </p>
          </div>
        </div>

        <div className="flex justify-start">
          <button
            onClick={handleSaveLoyaltyClick}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-2.5 rounded-md transition-colors text-sm disabled:opacity-60 shadow-sm"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
      
      <ConfirmDialog
        open={confirmModalOpen}
        title="Update Loyalty Settings"
        description="Are you sure you want to update the global loyalty points settings? These changes will immediately affect how points are earned and valued for all customers."
        variant="warning"
        confirmText="Yes, Update Settings"
        onConfirm={confirmSave}
        onCancel={() => setConfirmModalOpen(false)}
      />
    </div>
  );
};

export default LoyaltyPoints;
